import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useDisclosure,
  HStack,
} from '@chakra-ui/react';
import { supabase } from '../../supabaseClient';
import { useOutletContext, Navigate } from 'react-router-dom';

function AdminManagement() {
  const { adminUser } = useOutletContext();
  const [admins, setAdmins] = useState([]);
  const [newSecretCode, setNewSecretCode] = useState('');
  const [newName, setNewName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetAdminId, setResetAdminId] = useState(null);
  const [resetCode, setResetCode] = useState('');
  const [newRole, setNewRole] = useState('regular');
  const [newAgentEmail, setNewAgentEmail] = useState('');
  const [agentEmailEdits, setAgentEmailEdits] = useState({}); // adminId -> draft value
  const toast = useToast();

  const fetchAdmins = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAdmins(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching admins',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  // Only allow master admins (after all hooks)
  if (!adminUser || adminUser.role !== 'master') {
    return <Navigate to="/admin" replace />;
  }

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .insert([{
          name: newName,
          secret_code: newSecretCode,
          role: newRole,
          dialerr_agent_email: newAgentEmail.trim() || null
        }])
        .select();
      if (error) throw error;
      if (data && data.length > 0) {
        setAdmins(prevAdmins => [data[0], ...prevAdmins]);
        setNewSecretCode('');
        setNewName('');
        setNewRole('regular');
        setNewAgentEmail('');
        onClose();
        toast({
          title: 'Admin added successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error adding admin',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Map (or clear) an existing admin's Dialerr softphone agent email.
  const saveAgentEmail = async (adminId) => {
    const value = (agentEmailEdits[adminId] ?? '').trim();
    try {
      const { error } = await supabase
        .from('admin_users')
        .update({ dialerr_agent_email: value || null })
        .eq('id', adminId);
      if (error) throw error;
      setAdmins((prev) => prev.map((a) => (a.id === adminId ? { ...a, dialerr_agent_email: value || null } : a)));
      setAgentEmailEdits((prev) => { const next = { ...prev }; delete next[adminId]; return next; });
      toast({ title: value ? 'Softphone agent saved' : 'Softphone agent cleared', status: 'success', duration: 2000, isClosable: true });
    } catch (error) {
      toast({ title: 'Could not save agent email', description: error.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    try {
      // Clear references first — phleb profiles can be assigned to this admin,
      // and the FK blocks the delete otherwise.
      const { error: unlinkErr } = await supabase
        .from('phlebotomist_profiles')
        .update({ assigned_admin_id: null })
        .eq('assigned_admin_id', adminId);
      if (unlinkErr) throw unlinkErr;

      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', adminId);
      if (error) throw error;
      setAdmins(admins.filter(admin => admin.id !== adminId));
      toast({
        title: 'Admin deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error deleting admin',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openResetModal = (adminId) => {
    setResetAdminId(adminId);
    setResetCode('');
    setResetModalOpen(true);
  };

  const closeResetModal = () => {
    setResetModalOpen(false);
    setResetAdminId(null);
    setResetCode('');
  };

  const handleResetSecretCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    console.log('Resetting code for admin id:', resetAdminId, 'to:', resetCode);
    
    if (!resetCode.trim()) {
      toast({
        title: 'Error',
        description: 'Secret code cannot be empty',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
      return;
    }

    try {
      // First check if the admin exists
      const { data: existingAdmin, error: fetchError } = await supabase
        .from('admin_users')
        .select('id')
        .eq('id', resetAdminId)
        .single();

      if (fetchError) throw fetchError;
      if (!existingAdmin) {
        throw new Error('Admin not found');
      }

      // Then update the secret code
      const { error: updateError } = await supabase
        .from('admin_users')
        .update({ 
          secret_code: resetCode,
          updated_at: new Date().toISOString()
        })
        .eq('id', resetAdminId);

      if (updateError) throw updateError;

      toast({
        title: 'Secret code reset successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      closeResetModal();
      fetchAdmins();
    } catch (error) {
      console.error('Error resetting secret code:', error);
      toast({
        title: 'Error resetting secret code',
        description: error.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Button colorScheme="blue" mb={4} onClick={onOpen}>
        Add New Admin
      </Button>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Type</Th>
            <Th>Softphone agent (Dialerr email)</Th>
            <Th>Created At</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {admins.map((admin) => (
            <Tr key={admin.id}>
              <Td>{admin.name || 'N/A'}</Td>
              <Td>{admin.role === 'master' ? 'Master' : 'Regular'}</Td>
              <Td>
                <HStack spacing={2}>
                  <Input
                    size="sm"
                    w="220px"
                    type="email"
                    placeholder="agent@dialerr-email.com"
                    value={agentEmailEdits[admin.id] ?? admin.dialerr_agent_email ?? ''}
                    onChange={(e) => setAgentEmailEdits((prev) => ({ ...prev, [admin.id]: e.target.value }))}
                  />
                  {(agentEmailEdits[admin.id] !== undefined
                    && agentEmailEdits[admin.id] !== (admin.dialerr_agent_email ?? '')) && (
                    <Button size="sm" colorScheme="blue" onClick={() => saveAgentEmail(admin.id)}>Save</Button>
                  )}
                </HStack>
              </Td>
              <Td>{new Date(admin.created_at).toLocaleDateString()}</Td>
              <Td>
                <HStack spacing={2}>
                  {adminUser.role === 'master' && (
                    <Button
                      colorScheme="yellow"
                      size="sm"
                      onClick={() => openResetModal(admin.id)}
                    >
                      Reset Secret Code
                    </Button>
                  )}
                  <Button
                    colorScheme="red"
                    size="sm"
                    onClick={() => handleDeleteAdmin(admin.id)}
                  >
                    Delete
                  </Button>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Admin</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form onSubmit={handleAddAdmin}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    value={newName}
                    placeholder="Enter name"
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Secret Code</FormLabel>
                  <Input
                    value={newSecretCode}
                    onChange={(e) => setNewSecretCode(e.target.value)}
                    placeholder="Enter secret code"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Softphone agent email (optional)</FormLabel>
                  <Input
                    type="email"
                    value={newAgentEmail}
                    onChange={(e) => setNewAgentEmail(e.target.value)}
                    placeholder="Their Dialerr agent email"
                  />
                  <Box fontSize="sm" color="gray.600" mt={2}>
                    The email of this admin's <b>Dialerr agent</b>. Leave blank if they don't use the softphone.
                  </Box>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Type</FormLabel>
                  <select
                    value={newRole}
                    onChange={e => setNewRole(e.target.value)}
                    style={{ width: '100%', padding: 8, fontSize: 16 }}
                  >
                    <option value="regular">Regular</option>
                    <option value="master">Master</option>
                  </select>
                  <Box fontSize="sm" color="gray.600" mt={2}>
                    <b>Master:</b> Can manage admins and reset secret codes.<br />
                    <b>Regular:</b> Cannot manage admins or reset secret codes.
                  </Box>
                </FormControl>
                <Button
                  type="submit"
                  colorScheme="blue"
                  width="full"
                  isLoading={isLoading}
                >
                  Add Admin
                </Button>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={resetModalOpen} onClose={closeResetModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reset Secret Code</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form onSubmit={handleResetSecretCode}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>New Secret Code</FormLabel>
                  <Input
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    placeholder="Enter new secret code"
                  />
                </FormControl>
                <Button
                  type="submit"
                  colorScheme="yellow"
                  width="full"
                  isLoading={isLoading}
                >
                  Reset Secret Code
                </Button>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default AdminManagement; 